<template>
  <article class="article-card">
    <a :href="articleUrl" class="article-card__link">
      <div class="article-card__image" v-if="article.image || article.featuredImage">
        <img :src="article.image || article.featuredImage" :alt="article.title" />
      </div>
      <div class="article-card__content">
        <span class="article-card__category" v-if="article.category">
          {{ categoryLabel }}
        </span>
        <h3 class="article-card__title">{{ article.title }}</h3>
        <p class="article-card__excerpt" v-if="article.excerpt">
          {{ article.excerpt }}
        </p>
        <div class="article-card__meta">
          <span class="article-card__author" v-if="article.author">
            {{ article.author }}
          </span>
          <span class="article-card__date" v-if="article.publishDate || article.date">
            {{ formattedDate }}
          </span>
        </div>
        <div class="article-card__tags" v-if="article.tags && article.tags.length">
          <span
            v-for="tag in article.tags.slice(0, 3)"
            :key="tag"
            class="article-card__tag"
          >
            {{ tag }}
          </span>
        </div>
      </div>
    </a>
  </article>
</template>

<script>
export default {
  name: 'ArticleCard',
  props: {
    article: {
      type: Object,
      required: true,
    },
  },
  computed: {
    articleUrl() {
      const { category, slug } = this.article
      return `/${category}/${slug}/`
    },
    formattedDate() {
      const date = this.article.publishDate || this.article.date
      if (!date) return ''
      const d = new Date(date)
      return d.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
    },
    categoryLabel() {
      const labels = {
        news: 'News',
        reviews: 'Reviews',
        guides: 'Guides',
        deals: 'Deals',
      }
      return labels[this.article.category] || this.article.category
    },
  },
}
</script>

<style lang="scss" scoped>
@use '@/css/variables.scss' as *;

.article-card {
  background-color: $bg-primary;
  border: 1px solid $border-color;
  border-radius: 8px;
  overflow: hidden;
  transition: transform 0.2s, box-shadow 0.2s;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }

  &__link {
    display: block;
    text-decoration: none;
    color: inherit;
  }

  &__image {
    width: 100%;
    height: 200px;
    overflow: hidden;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  &__content {
    padding: $spacing-lg;
  }

  &__category {
    display: inline-block;
    padding: $spacing-xs $spacing-sm;
    background-color: $primary-color;
    color: white;
    border-radius: 4px;
    font-size: $font-size-sm;
    font-weight: 600;
    text-transform: uppercase;
    margin-bottom: $spacing-sm;
  }

  &__title {
    font-size: $font-size-lg;
    font-weight: 600;
    color: $text-primary;
    margin: 0 0 $spacing-sm 0;
    line-height: 1.4;
  }

  &__excerpt {
    font-size: $font-size-base;
    color: $text-secondary;
    line-height: 1.6;
    margin: 0 0 $spacing-md 0;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  &__meta {
    display: flex;
    gap: $spacing-sm;
    align-items: center;
    font-size: $font-size-sm;
    color: $text-secondary;
    margin-bottom: $spacing-sm;
  }

  &__author {
    font-weight: 600;
    color: $text-primary;
  }

  &__date {
    font-weight: 400;

    &::before {
      content: '•';
      margin-right: $spacing-sm;
    }
  }

  &__tags {
    display: flex;
    flex-wrap: wrap;
    gap: $spacing-xs;
  }

  &__tag {
    padding: $spacing-xs $spacing-sm;
    background-color: $bg-secondary;
    border-radius: 4px;
    font-size: $font-size-xs;
    color: $text-secondary;
    font-weight: 500;
  }
}
</style>
